import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import InputAdornment from '@material-ui/core/InputAdornment';
import Alert from '@material-ui/lab/Alert';
import moment from 'logic/moment';
import { validateHostOfferEditPatch, getMinMaxDates } from 'logic/bookingForm';
import { DatePicker, DateTimePicker, Input } from 'components/FormInputs';
import { useFormik } from 'components/shared/Formik';

const onlyNumbers = (value) => {
  if (value == null) {
    return '';
  }
  // Recover formatted user/paste inputs like "12 500", "5,000", "SEK 12500":
  // strip whitespace (incl. nbsp), comma thousands-separators, and known
  // currency tokens. Reject anything else (negatives, scientific notation,
  // stray letters) so "-100" / "5e3" come back as '' instead of silently
  // becoming positive 100 / 53. Decimals are accepted and truncated to the
  // integer part — "5000.5" becomes "5000" (not 5_000_500 öre when * 100).
  const normalized = String(value)
    .replace(/\s/g, '')
    .replace(/,/g, '')
    .replace(/SEK|EUR|USD|kr|[$€£¥]/gi, '');
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) {
    return '';
  }
  return normalized.split('.')[0];
};

const EditOfferForm = ({ onSubmit, booking, disabled }) => {
  const intl = useIntl();
  const dateRange = React.useMemo(() => getMinMaxDates(), []);
  const formik = useFormik({
    onSubmit: async (values) =>
      onSubmit({
        ...values,
        total_price: onlyNumbers(values.total_price),
      }),
    validate: validateHostOfferEditPatch({ intl }),
    initialValues: {
      check_in: booking.check_in,
      check_out: booking.check_out,
      total_price: Math.round(booking.total_price / 100),
      booking_expiration_date: booking.booking_expiration_date,
    },
    enableReinitialize: true,
  });

  const { values, errors, handleSubmit, setFieldValue, isSubmitting, isValid, handleBlur } = formik;

  // Formik's `dirty` flips true when a picker hands back a moment instance even
  // if the value is unchanged. Compare semantically so save stays disabled
  // until something real differs.
  const hasMeaningfulChange = React.useMemo(() => {
    const sameMinute = (a, b) => moment(a).isSame(moment(b), 'minute');
    const sameDay = (a, b) => moment(a).isSame(moment(b), 'day');
    return (
      !sameMinute(values.check_in, booking.check_in) ||
      !sameMinute(values.check_out, booking.check_out) ||
      !sameDay(values.booking_expiration_date, booking.booking_expiration_date) ||
      Number(values.total_price) !== Math.round(booking.total_price / 100)
    );
  }, [
    values.check_in,
    values.check_out,
    values.booking_expiration_date,
    values.total_price,
    booking.check_in,
    booking.check_out,
    booking.booking_expiration_date,
    booking.total_price,
  ]);

  return (
    <Box m={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Typography variant="body2" paragraph>
            <FormattedMessage
              id="bookingInfo.editOfferIntro"
              defaultMessage="You can change check-in and check-out dates and times, the total price, and the offer expiration date."
            />
          </Typography>
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DatePicker
                  name="booking_expiration_date"
                  value={values.booking_expiration_date || null}
                  onBlur={handleBlur}
                  onChange={(date) => setFieldValue('booking_expiration_date', date ?? null)}
                  label={
                    <FormattedMessage
                      id="bookingForm.offerValidTill"
                      defaultMessage="Time limit for guest to accept?"
                    />
                  }
                  cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
                  clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
                  errors={errors}
                  minDate={moment().format()}
                  maxDate={values.check_in ? moment(values.check_in).format() : dateRange.max}
                  required
                  disabled={disabled || !values.check_in}
                />
                <Typography variant="body2">
                  <FormattedMessage
                    id="bookingForm.expirationRecommendation"
                    defaultMessage="Guest accepts booking offer by paying the full rent"
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  name="check_in"
                  value={values.check_in || null}
                  onBlur={handleBlur}
                  onChange={(date) => setFieldValue('check_in', date ?? null)}
                  label={<FormattedMessage id="bookingForm.checkIn" defaultMessage="Check in" />}
                  cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
                  clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
                  errors={errors}
                  minDate={dateRange.min}
                  maxDate={dateRange.max}
                  required
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  name="check_out"
                  value={values.check_out || null}
                  onBlur={handleBlur}
                  onChange={(date) => setFieldValue('check_out', date ?? null)}
                  label={<FormattedMessage id="bookingForm.checkOut" defaultMessage="Check out" />}
                  cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
                  clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
                  errors={errors}
                  minDate={dateRange.min}
                  maxDate={dateRange.max}
                  required
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Input
                  name="total_price"
                  value={values.total_price}
                  errors={errors}
                  onChange={(e) => setFieldValue('total_price', onlyNumbers(e.target.value))}
                  onBlur={handleBlur}
                  label={
                    <FormattedMessage id="bookingForm.totalPrice" defaultMessage="Total price" />
                  }
                  required
                  type="text"
                  disabled={disabled}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">SEK</InputAdornment>,
                    inputProps: { inputMode: 'numeric', pattern: '[0-9]*' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined" style={{ display: 'inline-flex' }}>
                  <FormattedMessage
                    id="bookingInfo.datesOrPriceEmail"
                    defaultMessage="The guest receives an email when you change dates or price."
                  />
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={disabled || !hasMeaningfulChange || isSubmitting || !isValid}
                  type="submit"
                >
                  <FormattedMessage id="Save" defaultMessage="Save" />
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </Box>
  );
};

EditOfferForm.propTypes = {
  booking: PropTypes.shape({
    check_in: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])
      .isRequired,
    check_out: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])
      .isRequired,
    total_price: PropTypes.number.isRequired,
    booking_expiration_date: PropTypes.string.isRequired,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default EditOfferForm;
